import { formatDate, useResource } from '@landsbankinn/l16n';
import { Button, useMedia, theme } from '@landsbankinn/ui';
import React from 'react';
import type { fetchFunds_funds_nodes } from '../../../../graphql/__generated__/fetchFunds';
import {
  MarketsBodyBold,
  MarketsNumberSmall,
  MarketsSmall,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import { RiskRating, RiskRatingSize } from '../risk-rating/RiskRating';
import res from './../Funds.res';
import { ButtonContainer } from './useNominalData';

export function usePriceData(data: fetchFunds_funds_nodes[], category: string) {
  const { r, locale } = useResource();
  const isMobile = useMedia('sm');

  return React.useMemo(() => {
    if (isMobile) {
      return [
        {
          Header: category,
          accessor: 'shortName',
          headerStyle: {
            flexDirection: 'row-reverse',
          },
          Cell: ({ value, row: { original: fund } }: any) => (
            <MarketsBodyBold>
              {value}
              {fund.details?.ucitsClassification
                .toLocaleLowerCase()
                .includes('non-ucits') && <>*</>}
            </MarketsBodyBold>
          ),
        },
        {
          Header: r(res.currency),
          accessor: 'currency',
          style: {
            textAlign: 'right',
          },
          Cell: ({ value }: any) => <MarketsSmall>{value}</MarketsSmall>,
        },
        {
          Header: r(res.date),
          accessor: 'date',
          Cell: ({ value }: any) => (
            <MarketsSmall>{formatDate(locale, new Date(value))}</MarketsSmall>
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.bidRate),
          accessor: 'bid',
          Cell: ({ value }: any) => (
            <MarketsNumberSmall
              type={NumberType.Number}
              decimalScale={2}
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.askRate),
          accessor: 'ask',
          Cell: ({ value }: any) => (
            <MarketsNumberSmall
              type={NumberType.Number}
              decimalScale={2}
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.volatility),
          accessor: 'details.riskRating',
          Cell: ({ value }: any) => (
            <RiskRating
              value={value}
              size={isMobile ? RiskRatingSize.Small : RiskRatingSize.Normal}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
      ];
    } else {
      return [
        {
          Header: category,
          accessor: 'shortName',
          headerStyle: {
            flexDirection: 'row-reverse',
          },
          Cell: ({ value, row: { original: fund } }: any) => (
            <MarketsBodyBold>
              {value}
              {fund.details?.ucitsClassification
                .toLocaleLowerCase()
                .includes('non-ucits') && <>*</>}
            </MarketsBodyBold>
          ),
        },
        {
          Header: r(res.currency),
          accessor: 'currency',
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.date),
          accessor: 'date',
          Cell: ({ value }: any) => formatDate(locale, new Date(value)),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.bidRate),
          accessor: 'bid',
          Cell: ({ value }: any) => (
            <MarketsFormatNumber
              type={NumberType.Number}
              decimalScale={2}
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.askRate),
          accessor: 'ask',
          Cell: ({ value }: any) => (
            <MarketsFormatNumber
              type={NumberType.Number}
              decimalScale={2}
              number={value}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.volatility),
          accessor: 'details.riskRating',
          Cell: ({ value }: any) => (
            <RiskRating
              value={value}
              size={isMobile ? RiskRatingSize.Small : RiskRatingSize.Normal}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: '',
          accessor: 'details.onlineBuyAllowed',
          Cell: ({ value, row: { original: fund } }: any) =>
            value ? (
              <ButtonContainer>
                <Button
                  as="a"
                  title={r(res.buy)}
                  isSmall
                  href={
                    fund.symbol !== 'LEQ'
                      ? 'https://netbanki.landsbankinn.is/Securities/Overview/Overview.aspx#/funds/market'
                      : 'https://netbanki.landsbankinn.is/Securities/Overview/Overview.aspx#/stocks/market'
                  }
                  bg={theme.colors.primary}
                  onClick={(e) => e.stopPropagation()}
                />
              </ButtonContainer>
            ) : (
              <ButtonContainer></ButtonContainer>
            ),
          style: {
            textAlign: 'right',
          },
        },
      ];
    }
  }, [data]);
}
